import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { EventProvider, useEvent } from './contexts/EventContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import { MyScheduleProvider, useMySchedule } from './contexts/MyScheduleContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { QuickLinksProvider } from './contexts/QuickLinksContext';
import { AlertsProvider } from './contexts/AlertsContext';
import { Login } from './components/auth/Login';
import { SignUp } from './components/auth/SignUp';
import { Onboarding } from './components/auth/Onboarding';
import { AdminLogin } from './components/admin/auth/AdminLogin';
import { Header } from './components/layout/Header';
import { FloatingMenu } from './components/layout/FloatingMenu';
import { HomePage } from './components/HomePage';
import { ExhibitorList } from './components/ExhibitorList';
import { FloorPlan } from './components/FloorPlan';
import { Agenda } from './components/Agenda';
import { Networking } from './components/Networking';
import { Settings } from './components/Settings';
import { Profile } from './components/Profile';
import { EventPreference } from './components/EventPreference';
import { AdminLayout } from './components/admin/layout/AdminLayout';
import { Dashboard } from './components/admin/dashboard/Dashboard';
import { MySchedule } from './components/MySchedule';
import { Alerts } from './components/Alerts';
import { WhatsOn } from './components/WhatsOn';
// Extend Window interface for networking back handler
declare global {
  interface Window {
    __networkingBackHandler?: () => void;
  }
}
interface NavigationHistoryItem {
  page: string;
  meetingId?: string;
}
function AttendeeApp() {
  const {
    isAuthenticated,
    isOnboarding
  } = useAuth();
  const {
    bookedSessions,
    meetings
  } = useMySchedule();
  const {
    t
  } = useLanguage();
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | undefined>(undefined);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Global navigation history stack
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([{
    page: 'home'
  }]);
  // State for dynamic header updates from Networking
  const [networkingHeaderTitle, setNetworkingHeaderTitle] = useState('Networking');
  const [networkingHeaderSubtitle, setNetworkingHeaderSubtitle] = useState<string | undefined>('Connect with industry professionals');
  // Enhanced navigate function that tracks history
  const handleNavigate = (page: string, options?: {
    meetingId?: string;
    replaceHistory?: boolean;
    popHistory?: boolean;
    fromMenu?: boolean;
  }) => {
    if (options?.popHistory) {
      // Pop from history and navigate to previous page
      if (navigationHistory.length > 1) {
        const newHistory = [...navigationHistory];
        newHistory.pop(); // Remove current page
        const previousItem = newHistory[newHistory.length - 1];
        setNavigationHistory(newHistory);
        setCurrentPage(previousItem.page);
        if (previousItem.meetingId) {
          setSelectedMeetingId(previousItem.meetingId);
        } else {
          setSelectedMeetingId(undefined);
        }
      }
      return;
    }
    const newHistoryItem: NavigationHistoryItem = {
      page,
      meetingId: options?.meetingId
    };
    if (options?.fromMenu) {
      // When navigating from menu, reset history to just home + new page
      setNavigationHistory([{
        page: 'home'
      }, newHistoryItem]);
    } else if (options?.replaceHistory) {
      // Replace current history item (for sub-navigation within same page)
      setNavigationHistory(prev => [...prev.slice(0, -1), newHistoryItem]);
    } else {
      // Add new history item
      setNavigationHistory(prev => [...prev, newHistoryItem]);
    }
    setCurrentPage(page);
    if (options?.meetingId) {
      setSelectedMeetingId(options.meetingId);
    } else if (page !== 'networking') {
      setSelectedMeetingId(undefined);
      // Reset networking header state
      setNetworkingHeaderTitle(t('nav.networking'));
      setNetworkingHeaderSubtitle(t('networking.subtitle'));
    }
  };
  // Not authenticated - show auth flow
  if (!isAuthenticated) {
    if (authView === 'login') {
      return <Login onSwitchToSignup={() => setAuthView('signup')} data-id="element-0" />;
    }
    return <SignUp onSwitchToLogin={() => setAuthView('login')} data-id="element-1" />;
  }
  // Authenticated but needs onboarding
  if (isOnboarding) {
    return <Onboarding data-id="element-2" />;
  }
  const handleBack = () => {
    // If on networking page and there's an internal back handler, try it first
    if (currentPage === 'networking' && window.__networkingBackHandler) {
      const shouldExitNetworking = window.__networkingBackHandler();
      // If the handler returns true, it means we should exit Networking
      if (shouldExitNetworking) {
        // Pop from global history
        if (navigationHistory.length > 1) {
          const newHistory = [...navigationHistory];
          newHistory.pop();
          const previousItem = newHistory[newHistory.length - 1];
          setNavigationHistory(newHistory);
          setCurrentPage(previousItem.page);
          if (previousItem.meetingId) {
            setSelectedMeetingId(previousItem.meetingId);
          } else {
            setSelectedMeetingId(undefined);
          }
        }
      }
      return;
    }
    // For other pages, use global navigation history
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const previousItem = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setCurrentPage(previousItem.page);
      if (previousItem.meetingId) {
        setSelectedMeetingId(previousItem.meetingId);
      } else {
        setSelectedMeetingId(undefined);
      }
    }
  };
  // Handler for Networking to update header dynamically
  const handleNetworkingHeaderUpdate = (title: string, subtitle?: string) => {
    setNetworkingHeaderTitle(title);
    setNetworkingHeaderSubtitle(subtitle);
  };
  // Handler for Networking/Agenda to navigate to schedule
  const handleNavigateToSchedule = () => {
    handleNavigate('favorites');
  };
  // Get page-specific header props with translations
  const getHeaderProps = () => {
    const totalScheduledItems = bookedSessions.length + meetings.length;
    switch (currentPage) {
      case 'home':
        return {
          showBackButton: false,
          showEventInfo: true
        };
      case 'exhibitors':
        return {
          showBackButton: true,
          pageTitle: t('nav.exhibitors'),
          showEventInfo: false
        };
      case 'floorplan':
        return {
          showBackButton: true,
          pageTitle: t('nav.floorplan'),
          showEventInfo: false
        };
      case 'agenda':
        return {
          showBackButton: true,
          pageTitle: t('nav.agenda'),
          pageSubtitle: `${bookedSessions.length} ${bookedSessions.length === 1 ? t('agenda.session') : t('agenda.sessions')} ${t('agenda.scheduled')}`,
          showEventInfo: false
        };
      case 'networking':
        return {
          showBackButton: true,
          pageTitle: networkingHeaderTitle,
          pageSubtitle: networkingHeaderSubtitle,
          showEventInfo: false
        };
      case 'favorites':
        return {
          showBackButton: true,
          pageTitle: t('nav.schedule'),
          pageSubtitle: `${totalScheduledItems} ${totalScheduledItems === 1 ? t('schedule.item') : t('schedule.items')} ${t('schedule.scheduled')}`,
          showEventInfo: false
        };
      case 'alerts':
        return {
          showBackButton: true,
          pageTitle: t('nav.alerts'),
          pageSubtitle: t('alerts.subtitle'),
          showEventInfo: false
        };
      case 'whatson':
        return {
          showBackButton: true,
          pageTitle: t('nav.whatson'),
          pageSubtitle: t('whatson.subtitle'),
          showEventInfo: false
        };
      case 'buyers':
        return {
          showBackButton: true,
          pageTitle: t('nav.buyers'),
          showEventInfo: false
        };
      case 'settings':
        return {
          showBackButton: true,
          pageTitle: t('nav.settings'),
          pageSubtitle: t('settings.subtitle'),
          showEventInfo: false
        };
      case 'profile':
        return {
          showBackButton: true,
          pageTitle: t('settings.profile'),
          showEventInfo: false
        };
      case 'eventPreference':
        return {
          showBackButton: true,
          pageTitle: t('settings.eventPreference'),
          showEventInfo: false
        };
      default:
        return {
          showBackButton: false,
          showEventInfo: true
        };
    }
  };
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} data-id="element-3" />;
      case 'exhibitors':
        return <ExhibitorList onNavigate={handleNavigate} data-id="element-4" />;
      case 'floorplan':
        return <FloorPlan data-id="element-5" />;
      case 'agenda':
        return <Agenda onNavigateToSchedule={handleNavigateToSchedule} data-id="element-6" />;
      case 'favorites':
        return <MySchedule onBack={() => handleNavigate('home')} onNavigateToMeetingDetails={meetingId => {
          handleNavigate('networking', {
            meetingId
          });
        }} data-id="element-7" />;
      case 'alerts':
        return <Alerts onNavigateToNetworking={() => handleNavigate('networking')} onNavigateToAgenda={() => handleNavigate('agenda')} onNavigateToSchedule={() => handleNavigate('favorites')} data-id="element-8" />;
      case 'whatson':
        return <WhatsOn data-id="element-9" />;
      case 'networking':
        return <Networking sessions={[]} exhibitors={[]} onNavigateToSchedule={handleNavigateToSchedule} initialMeetingId={selectedMeetingId} cameFromSchedule={!!selectedMeetingId} onUpdateHeader={handleNetworkingHeaderUpdate} onNavigateBack={handleBack} data-id="element-10" />;
      case 'buyers':
        return <div className="p-4 text-center text-gray-500" data-id="element-11">
            <p data-id="element-12">Buyers list coming soon</p>
          </div>;
      case 'settings':
        return <Settings onNavigateToProfile={() => handleNavigate('profile')} onNavigateToEventPreference={() => handleNavigate('eventPreference')} data-id="element-13" />;
      case 'profile':
        return <Profile onBack={handleBack} data-id="element-14" />;
      case 'eventPreference':
        return <EventPreference onBack={handleBack} data-id="element-15" />;
      default:
        return <HomePage onNavigate={handleNavigate} data-id="element-16" />;
    }
  };
  const headerProps = getHeaderProps();
  return <div className="min-h-screen bg-white" data-id="element-17">
      <Header onBack={handleBack} onMenuClick={() => setIsMenuOpen(true)} {...headerProps} data-id="element-18" />
      <main className="w-full max-w-2xl mx-auto" data-id="element-19">{renderPage()}</main>
      <FloatingMenu onNavigate={page => handleNavigate(page, {
      fromMenu: true
    })} currentPage={currentPage} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} data-id="element-20" />
    </div>;
}
function AdminApp() {
  const {
    isAdminAuthenticated
  } = useAdminAuth();
  if (!isAdminAuthenticated) {
    return <AdminLogin data-id="element-21" />;
  }
  return <AdminLayout data-id="element-22" />;
}
function AppContent() {
  return <AttendeeApp data-id="element-23" />;
}
export function App() {
  return <BrowserRouter data-id="element-24">
      <LanguageProvider data-id="element-25">
        <AuthProvider data-id="element-26">
          <EventProvider data-id="element-27">
            <MyScheduleProvider data-id="element-28">
              <AlertsProvider data-id="element-29">
                <QuickLinksProvider data-id="element-30">
                  <Routes data-id="element-31">
                    {/* Main attendee app */}
                    <Route path="/*" element={<AppContent data-id="element-33" />} data-id="element-32" />

                    {/* Separate admin route with its own auth */}
                    <Route path="/admin/*" element={<AdminAuthProvider data-id="element-35">
                          <AdminApp data-id="element-36" />
                        </AdminAuthProvider>} data-id="element-34" />
                  </Routes>
                </QuickLinksProvider>
              </AlertsProvider>
            </MyScheduleProvider>
          </EventProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>;
}